import { useEffect, useRef, useState, Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, MapPin, Loader2, LifeBuoy, Truck, PackageOpen, ChevronRight } from "lucide-react";
import { CategoryIcon } from "@/components/CategoryIcon";
import BusinessCard from "@/components/BusinessCard";
import LiveRescuers from "@/components/LiveRescuers";
import HelpRequestModal from "@/components/HelpRequestModal";
import CustomerTracking from "@/components/CustomerTracking";
import ReviewModal from "@/components/ReviewModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { mergeHome } from "@/lib/homeContent";
import { HomeBlock, useHomeSeo } from "@/components/HomeBlocks";

const EXAMPLES = ["oto kurtarıcı", "yol yardım", "lowbed", "mobil lastikçi", "ağır vasıta kurtarma", "araç taşıma"];

const PILLAR_COLS = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3" };

export default function Home({ previewContent }) {
  const [cms, setCms] = useState(previewContent ? mergeHome(previewContent) : mergeHome(null));
  useEffect(() => {
    if (previewContent) { setCms(mergeHome(previewContent)); return; }
    api.get("/cms/home").then(({ data }) => setCms(mergeHome(data?.content))).catch(() => {});
  }, [previewContent]);
  const hero = cms.hero;
  useHomeSeo(cms.seo, cms.blocks);
  const pillarOf = (k) => cms.pillars.items.find((i) => i.key === k);
  const visiblePillars = cms.pillars.items.filter((i) => i.visible).length;
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [province, setProvince] = useState("");
  const [district, setDistrict] = useState("");
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [gettingLoc, setGettingLoc] = useState(false);
  const [pos, setPos] = useState(null);
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [helpTarget, setHelpTarget] = useState(null);
  const [activeRequest, setActiveRequest] = useState(null);
  const [pendingReview, setPendingReview] = useState(null);
  const prevActiveIdRef = useRef(null);
  const liveSectionRef = useRef(null);
  const trackingRef = useRef(null);
  const pollRef = useRef(null);
  const requestPollRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/locations/provinces").then(({ data }) => setProvinces(data));
    api.get("/businesses/featured").then(({ data }) => setFeatured(data));
  }, []);

  // Load & poll customer's active help request
  const loadActiveRequest = async () => {
    if (!user || user.role !== "user") return;
    try {
      const { data } = await api.get("/help-requests/mine");
      // Detect transition: had active → now null → check for pending-review
      if (prevActiveIdRef.current && !data) {
        try {
          const { data: pr } = await api.get("/help-requests/pending-review");
          if (pr && pr.id === prevActiveIdRef.current) {
            toast.success("Yardım talebiniz tamamlandı. Geçmiş olsun.");
            setTimeout(() => setPendingReview(pr), 1500);
          }
        } catch (err) {
          // No pending review yet — silent (expected 404 path).
          if (err?.response?.status && err.response.status !== 404) {
            console.warn("[Home.pendingReview]", err.response.status);
          }
        }
      }
      prevActiveIdRef.current = data?.id || null;
      setActiveRequest(data);
    } catch (err) {
      // Poll — background, not user-actionable.
      console.warn("[Home.loadActiveRequest]", err?.response?.status || err?.message);
    }
  };
  useEffect(() => {
    if (!user || user.role !== "user") { setActiveRequest(null); return; }
    loadActiveRequest();
    requestPollRef.current = setInterval(loadActiveRequest, 5000);
    return () => { if (requestPollRef.current) clearInterval(requestPollRef.current); };
    // eslint-disable-next-line
  }, [user?.id]);

  useEffect(() => {
    if (province) api.get("/locations/districts", { params: { province } }).then(({ data }) => setDistricts(data));
    else setDistricts([]);
    setDistrict("");
  }, [province]);

  const loadLive = async (loc) => {
    if (!loc) return;
    try {
      setLiveLoading(true);
      const { data } = await api.get("/live/rescuers", { params: { lat: loc.lat, lng: loc.lng, radius_km: 50, limit: 20 } });
      setLive(data);
    } catch (e) {
      console.warn("live fetch failed", e);
    } finally {
      setLiveLoading(false);
    }
  };

  // Auto-poll every 15s while a live location exists
  useEffect(() => {
    if (!pos) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadLive(pos), 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pos]);

  const scrollToLive = () => {
    setTimeout(() => {
      liveSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Tarayıcınız konum servisini desteklemiyor.");
    setGettingLoc(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const loc = { lat: p.coords.latitude, lng: p.coords.longitude };
        setPos(loc);
        setGettingLoc(false);
        toast.success("Konumunuz alındı. Aktif kurtarıcılar listeleniyor...");
        await loadLive(loc);
        scrollToLive();
      },
      () => {
        setGettingLoc(false);
        toast.error("Konum alınamadı. Tarayıcınızın konum iznini kontrol edin veya aşağıdan haritadan/manuel adres seçin.");
      },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  const submit = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (province) params.set("province", province);
    if (district) params.set("district", district);
    if (pos) { params.set("user_lat", pos.lat); params.set("user_lng", pos.lng); }
    nav(`/arama?${params.toString()}`);
  };

  const heroBlock = (
    <>
      <section className="relative overflow-hidden brand-hero text-white" data-testid="home-hero"
        style={hero.background_image_url ? { backgroundImage: `linear-gradient(rgba(10,10,10,.78), rgba(10,10,10,.78)), url("${hero.background_image_url}")`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #FFC107 0px, transparent 30%), radial-gradient(circle at 80% 80%, #FFC107 0px, transparent 25%)" }} />
        <div className="mx-auto max-w-5xl px-4 pt-10 pb-8 sm:pt-16 sm:pb-14 relative">
          {hero.badge_text && <div className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1 rounded-full bg-primary text-[hsl(var(--brand-black))] mb-4" data-testid="home-hero-badge">
            <LifeBuoy className="w-3.5 h-3.5" /> {hero.badge_text}
          </div>}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="home-hero-title">
            {hero.title.split("\n").map((l, i) => <Fragment key={i}>{i > 0 && <br />}{l}</Fragment>)}
            {hero.title_highlight && <><br /><span className="text-primary">{hero.title_highlight}</span></>}
          </h1>
          {hero.subtitle && <p className="text-white/70 mt-4 text-base sm:text-lg max-w-2xl" data-testid="home-hero-subtitle">
            {hero.subtitle}
          </p>}
          {hero.cta_text && hero.cta_link && (
            hero.cta_link.startsWith("/")
              ? <Link to={hero.cta_link} data-testid="home-hero-cta" className="inline-flex mt-4 rounded-full bg-primary text-[hsl(var(--brand-black))] px-5 py-2 font-bold hover:brightness-110">{hero.cta_text}</Link>
              : <a href={hero.cta_link} target="_blank" rel="noopener noreferrer" data-testid="home-hero-cta" className="inline-flex mt-4 rounded-full bg-primary text-[hsl(var(--brand-black))] px-5 py-2 font-bold hover:brightness-110">{hero.cta_text}</a>
          )}

          <form onSubmit={submit} className="bg-white rounded-3xl border border-border p-3 sm:p-4 shadow-2xl mt-6">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="home-search-input"
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Ne arıyorsunuz? Örn: oto kurtarıcı, lowbed..."
                  className="pl-9 h-12 rounded-2xl text-foreground"
                />
              </div>
              <Button
                type="button" data-testid="use-location-btn"
                variant="outline" className="h-12 rounded-2xl press text-foreground"
                onClick={useMyLocation} disabled={gettingLoc}
              >
                {gettingLoc ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MapPin className="w-4 h-4 mr-1" />}
                Konumumu Kullan
              </Button>
              <Button type="submit" data-testid="search-submit-btn" className="h-12 rounded-2xl px-6 press bg-[hsl(var(--brand-black))] hover:bg-[hsl(var(--brand-black))]/90 text-white">Ara</Button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Select value={province} onValueChange={setProvince}>
                <SelectTrigger data-testid="province-select" className="h-11 rounded-2xl"><SelectValue placeholder="İl seçin" /></SelectTrigger>
                <SelectContent>{provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={district} onValueChange={setDistrict} disabled={!province}>
                <SelectTrigger data-testid="district-select" className="h-11 rounded-2xl"><SelectValue placeholder="İlçe" /></SelectTrigger>
                <SelectContent>{districts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <span className="text-xs text-muted-foreground pt-1 mr-1">Örnek:</span>
              {EXAMPLES.map((s) => (
                <button type="button" key={s} onClick={() => setQ(s)}
                  data-testid={`example-${s.replace(/\s/g, "-")}`}
                  className="text-xs px-3 py-1 rounded-full border border-border bg-secondary hover:bg-secondary/70 text-foreground press">
                  {s}
                </button>
              ))}
            </div>
          </form>
        </div>
        <div className="h-1 brand-stripe" />
      </section>

      {/* Active help request tracking (customer) */}
      <div ref={trackingRef} data-testid="active-request-section">
        {activeRequest && ["pending","accepted","driver_arriving","arrived"].includes(activeRequest.status) && (
          <CustomerTracking request={activeRequest} onEnded={() => { setActiveRequest(null); loadActiveRequest(); }} onRefresh={loadActiveRequest} />
        )}
      </div>

      {/* Live Rescuers section - always visible so Acil Yol Yardım is reachable
          even before the customer grants geolocation permission. Map/list is
          populated once `pos` is available. */}
      <div ref={liveSectionRef} data-testid="live-section">
        {!pos && (
          <div
            data-testid="live-no-location-hint"
            className="rounded-2xl border-2 border-dashed border-primary/50 bg-primary/5 p-5 my-3 flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <div className="w-11 h-11 rounded-xl bg-primary text-[hsl(var(--brand-black))] grid place-items-center shrink-0">
              <LifeBuoy className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-base">Acil Yol Yardım</div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Yakınınızdaki aktif kurtarıcıları listeleyip yardım talebi
                gönderebilmemiz için önce konumunuzu paylaşın.
              </p>
            </div>
            <Button
              data-testid="live-request-location-btn"
              onClick={useMyLocation}
              disabled={gettingLoc}
              className="rounded-full bg-[hsl(var(--brand-black))] hover:bg-[hsl(var(--brand-black))]/90 text-white shrink-0"
            >
              {gettingLoc ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Konum alınıyor…</>
              ) : (
                <><MapPin className="w-4 h-4 mr-1" /> Konumumu Kullan</>
              )}
            </Button>
          </div>
        )}
        {pos && (
          <LiveRescuers
            data={live}
            userLocation={[pos.lat, pos.lng]}
            onRefresh={() => loadLive(pos)}
            refreshing={liveLoading}
            categories={categories}
            onHelpClick={(item) => {
              if (!user) { toast.error("Yardım istemek için giriş yapın"); nav("/giris"); return; }
              if (user.role !== "user") { toast.error("Yardım talebi sadece müşteri hesabıyla gönderilebilir"); return; }
              if (activeRequest) { toast.error("Zaten aktif bir yardım talebiniz var"); return; }
              setHelpTarget(item);
            }}
            disableHelp={!!activeRequest}
          />
        )}
      </div>

      <HelpRequestModal
        open={!!helpTarget}
        onOpenChange={(v) => !v && setHelpTarget(null)}
        target={helpTarget}
        userLocation={pos ? [pos.lat, pos.lng] : null}
        onCreated={(r) => { setActiveRequest(r); setTimeout(() => trackingRef.current?.scrollIntoView({ behavior: "smooth" }), 100); }}
      />

      <ReviewModal
        open={!!pendingReview}
        onOpenChange={(v) => !v && setPendingReview(null)}
        request={pendingReview}
        onDone={() => setPendingReview(null)}
      />
    </>
  );

  const pillarsBlock = cms.pillars.visible && visiblePillars > 0 && (
      <section className="mx-auto max-w-6xl px-4 py-8" data-testid="home-pillars">
        <div className={`grid grid-cols-1 ${PILLAR_COLS[visiblePillars]} gap-3`}>
          {pillarOf("emergency").visible && <button
            type="button"
            onClick={useMyLocation}
            data-testid="pillar-emergency"
            className="press group rounded-2xl bg-[hsl(var(--brand-black))] text-white p-5 flex flex-col justify-between min-h-[160px] hover:brightness-110 text-left"
          >
            <div>
              <div className="w-11 h-11 rounded-xl bg-primary text-[hsl(var(--brand-black))] grid place-items-center"><LifeBuoy className="w-6 h-6" /></div>
              <h3 className="mt-3 text-xl font-bold">{pillarOf("emergency").title}</h3>
              <p className="text-sm text-white/70 mt-1">{pillarOf("emergency").description}</p>
            </div>
            <div className="text-sm text-primary font-semibold flex items-center gap-1 mt-3">{pillarOf("emergency").cta_text} <ChevronRight className="w-4 h-4" /></div>
          </button>}
          {pillarOf("transport").visible && <button type="button" onClick={() => nav(user?.role === "business_owner" ? "/tasima-pazari" : "/tasima-yaptir")} data-testid="pillar-transport" className="press group rounded-2xl border-2 border-primary bg-primary/5 p-5 flex flex-col justify-between min-h-[160px] text-left hover:bg-primary/10">
            <div>
              <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary grid place-items-center"><Truck className="w-6 h-6" /></div>
              <h3 className="mt-3 text-xl font-bold">{pillarOf("transport").title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{pillarOf("transport").description}</p>
            </div>
            <div className="text-xs font-semibold text-primary mt-3">{user?.role === "business_owner" ? "İş Havuzuna Git" : pillarOf("transport").cta_text} <ChevronRight className="w-4 h-4 inline" /></div>
          </button>}
          {pillarOf("return").visible && <button type="button" onClick={() => nav(user?.role === "business_owner" ? "/tasima-pazari?tab=ers" : "/bos-donus-ara")} data-testid="pillar-return" className="press group rounded-2xl border-2 border-primary/60 bg-card p-5 flex flex-col justify-between min-h-[160px] text-left hover:bg-primary/5">
            <div>
              <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary grid place-items-center"><PackageOpen className="w-6 h-6" /></div>
              <h3 className="mt-3 text-xl font-bold">{pillarOf("return").title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{user?.role === "business_owner" ? "Boş dönüşünü yayınla" : pillarOf("return").description}</p>
            </div>
            <div className="text-xs font-semibold text-primary mt-3">{user?.role === "business_owner" ? "Yayınla" : pillarOf("return").cta_text} <ChevronRight className="w-4 h-4 inline" /></div>
          </button>}
        </div>
      </section>
  );

  const categoriesBlock = cms.categories.visible && (
      <section className="mx-auto max-w-6xl px-4 py-6" data-testid="home-categories">
        <h2 className={`text-xl sm:text-2xl font-bold tracking-tight ${cms.categories.subtitle ? "mb-1" : "mb-3"}`}>{cms.categories.title}</h2>
        {cms.categories.subtitle && <p className="text-xs text-muted-foreground mb-3">{cms.categories.subtitle}</p>}
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3">
          {categories.map((c) => <CategoryIcon key={c.id} category={c} />)}
        </div>
      </section>
  );

  const featuredBlock = cms.featured.visible && featured.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 py-6" data-testid="home-featured">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-1">{cms.featured.title}</h2>
          {cms.featured.subtitle && <p className="text-xs text-muted-foreground mb-3">{cms.featured.subtitle}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.slice(0, cms.featured.max_items).map((b) => <BusinessCard key={b.id} business={b} categories={categories} />)}
          </div>
        </section>
  );

  const blocks = { hero: heroBlock, pillars: pillarsBlock, categories: categoriesBlock, featured: featuredBlock };
  return (
    <div>
      {cms.sections_order.map((k) => <Fragment key={k}>{blocks[k] ?? (() => { const b = cms.blocks.find((x) => x.id === k); return b ? <HomeBlock b={b} preview={!!previewContent} /> : null; })()}</Fragment>)}

      <section className="mx-auto max-w-6xl px-4 pb-10">
        <Link
          to="/kayit"
          data-testid="home-cta-register"
          className="block rounded-2xl bg-primary text-white px-6 py-6 text-center text-2xl sm:text-3xl font-extrabold tracking-wide uppercase shadow-lg hover:brightness-110 transition"
        >
          ÜCRETSİZ ÜYE OLUN
        </Link>
      </section>
    </div>
  );
}
